import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/use-toast';
import { Upload, Database, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import * as Entities from "@/api/entities";

const ENTITIES = [
  { value: 'AccountFinal', label: 'Accounts' },
  { value: 'PersonFinal', label: 'People' },
  { value: 'MomentumScore', label: 'Momentum Scores' },
  { value: 'PersonaActivity', label: 'Persona Activities' },
  { value: 'Procurement', label: 'Procurement' },
  { value: 'Deal', label: 'Deals' },
];

const TENANTS = [
  { value: 'instructure', label: 'Instructure' },
  { value: 'clairelabs', label: 'Claire Labs' },
  { value: 'demarque', label: 'Demarque' },
  { value: 'demarque_V2', label: 'Demarque V2' },
  { value: 'smowl', label: 'Smowl' },
  { value: 'diambra', label: 'Diambra' },
  { value: 'global', label: 'Global' },
];

export default function BulkDataManager() {
  const [selectedEntity, setSelectedEntity] = useState('');
  const [selectedTenant, setSelectedTenant] = useState('instructure');
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
    } else {
      toast({
        title: 'Invalid file',
        description: 'Please select a CSV file',
        variant: 'destructive'
      });
    }
  };

  const handleUpload = async () => {
    if (!file || !selectedEntity) {
      toast({
        title: 'Missing information',
        description: 'Please select both an entity and a file',
        variant: 'destructive'
      });
      return;
    }

    setIsUploading(true);

    try {
      // Parse CSV
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim());
      
      const records = lines.slice(1).map(line => {
        const values = line.split(',');
        const record = { tenant_id: selectedTenant };
        headers.forEach((header, i) => {
          const value = values[i]?.trim();
          if (value) {
            record[header] = value;
          }
        });
        return record;
      });


      const Entity = Entities[selectedEntity];
      if (!Entity) {
        throw new Error(`Unknown entity: ${selectedEntity}`);
      }
            
      // Bulk create
      await Entity.bulkCreate(records);

      toast({
        title: 'Success!',
        description: `Uploaded ${records.length} records to ${selectedEntity} (tenant: ${selectedTenant})`
      });

      setFile(null);
      setSelectedEntity('');
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload failed',
        description: error.message || 'An error occurred during upload',
        variant: 'destructive'
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="w-5 h-5" />
          Bulk Data Manager
        </CardTitle>
        <CardDescription>
          Upload CSV files to bulk create records. Admin only.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            CSV files must have headers matching the entity fields. All uploaded records will be assigned to the selected tenant.
          </AlertDescription>
        </Alert>

        <div className="space-y-2">
          <Label htmlFor="entity">Entity</Label>
          <Select value={selectedEntity} onValueChange={setSelectedEntity}>
            <SelectTrigger id="entity">
              <SelectValue placeholder="Select entity" />
            </SelectTrigger>
            <SelectContent>
              {ENTITIES.map(entity => (
                <SelectItem key={entity.value} value={entity.value}>
                  {entity.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="tenant">Tenant</Label>
          <Select value={selectedTenant} onValueChange={setSelectedTenant}>
            <SelectTrigger id="tenant">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TENANTS.map(tenant => (
                <SelectItem key={tenant.value} value={tenant.value}>
                  {tenant.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="file">CSV File</Label>
          <Input
            id="file"
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            disabled={isUploading}
          />
          {file && (
            <p className="text-sm text-gray-600">
              Selected: {file.name}
            </p>
          )}
        </div>

        <Button
          onClick={handleUpload}
          disabled={!file || !selectedEntity || isUploading}
          className="w-full"
        >
          {isUploading ? (
            'Uploading...'
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Upload CSV
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}